import {
  Button,
  DatePicker,
  Form,
  Input,
  Select,
  Space,
  TimePicker,
} from 'antd';
import React, { useEffect } from 'react';

import { TrashCanIcon } from '@/components/UI/iconComponents';

import { EDUTAINMENT_PLACE_OPTIONS } from '../constants';
import { validateTimeRange } from '../helpers';
import type { NonFixedTime } from '../types';
import { validateNonFixedTimeRange } from './helpers';
import styles from './index.module.scss';

const { Item, List, useWatch, useFormInstance } = Form;

const DEFAULT_NON_FIXED_TIME_FIELD = {
  date: null,
  startTime: null,
  endTime: null,
  place: null,
  address: null,
};

export const NonFixedSchedule = () => {
  const form = useFormInstance();

  const nonFixedSchedule = useWatch<NonFixedTime[] | undefined>(
    'nonFixedSchedule'
  );

  useEffect(() => {
    return () => {
      form.resetFields(['nonFixedSchedule']);
    };
  }, [form]);

  useEffect(() => {
    if (!nonFixedSchedule) return;

    nonFixedSchedule.forEach((field, index) => {
      if (!field.date || !field.startTime || !field.endTime) {
        return;
      }
      form.validateFields([
        ['nonFixedSchedule', index, 'startTime'],
        ['nonFixedSchedule', index, 'endTime'],
      ]);
    });
  }, [form, nonFixedSchedule]);

  // TODO: 非固定時間的地點，看是要用哪些資料組成
  const placeOptions = [
    ...EDUTAINMENT_PLACE_OPTIONS,
    { label: '其他', value: '其他' },
  ];

  return (
    <>
      <p>非固定時間</p>
      <List name="nonFixedSchedule">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => {
              const shouldDisableAddressInput =
                nonFixedSchedule?.[index]?.place !== '其他';

              return (
                <div className={styles.nonFixedTimeField} key={field.key}>
                  <Space align="baseline">
                    <p>{`${index + 1}.`}</p>
                    <div className={styles.fieldSubTitle}>
                      <p>日期：</p>
                      <Item
                        name={[field.name, 'date']}
                        rules={[{ required: true, message: '必填欄位' }]}
                      >
                        <DatePicker
                          placeholder="請選擇日期"
                          format="YYYY/MM/DD(dddd)"
                        />
                      </Item>
                    </div>
                  </Space>

                  <Space align="baseline">
                    <div className={styles.fieldSubTitle}>
                      <p>時間：</p>
                      <Item
                        name={[field.name, 'startTime']}
                        rules={[
                          {
                            required: true,
                            validator: (_, startTime) =>
                              validateNonFixedTimeRange({
                                promise: validateTimeRange({
                                  isStartTime: true,
                                  startTime,
                                  endTime: form.getFieldValue([
                                    'nonFixedSchedule',
                                    field.name,
                                    'endTime',
                                  ]),
                                  errorMessage: '起始時間需早於結束時間',
                                }),
                                nonFixedSchedule,
                                validatingFieldName: field.name,
                              }),
                          },
                        ]}
                      >
                        <TimePicker
                          placeholder="請選擇起始時間"
                          className={styles.timePicker}
                          format="HH:mm"
                        />
                      </Item>
                    </div>
                    <Item
                      name={[field.name, 'endTime']}
                      rules={[
                        {
                          required: true,
                          validator: (_, endTime) =>
                            validateNonFixedTimeRange({
                              promise: validateTimeRange({
                                isStartTime: false,
                                startTime: form.getFieldValue([
                                  'nonFixedSchedule',
                                  field.name,
                                  'startTime',
                                ]),
                                endTime,
                                errorMessage: '結束時間需晚於起始時間',
                              }),
                              nonFixedSchedule,
                              validatingFieldName: field.name,
                            }),
                        },
                      ]}
                    >
                      <TimePicker
                        placeholder="請選擇結束時間"
                        className={styles.timePicker}
                        format="HH:mm"
                      />
                    </Item>
                  </Space>

                  <Space align="baseline" className={styles.placeContainer}>
                    <div className={styles.fieldSubTitle}>
                      <p>地點：</p>
                      <Item
                        name={[field.name, 'place']}
                        rules={[{ required: true, message: '必填欄位' }]}
                      >
                        <Select
                          options={placeOptions}
                          placeholder="請選擇育樂地點"
                          className={styles.selector}
                          onChange={(value) => {
                            if (value !== '其他') {
                              form.setFieldValue(
                                ['nonFixedSchedule', index, 'address'],
                                null
                              );
                            }
                          }}
                        />
                      </Item>
                    </div>
                    <Item
                      name={[field.name, 'address']}
                      rules={[
                        {
                          required: !shouldDisableAddressInput,
                          message: '必填欄位',
                        },
                      ]}
                    >
                      <Input
                        placeholder="請輸入地址"
                        disabled={shouldDisableAddressInput}
                      />
                    </Item>
                    {fields.length > 1 && (
                      <Button type="text" onClick={() => remove(index)}>
                        <TrashCanIcon />
                      </Button>
                    )}
                  </Space>
                </div>
              );
            })}
            <Button onClick={() => add(DEFAULT_NON_FIXED_TIME_FIELD)}>
              新增時間區段
            </Button>
          </>
        )}
      </List>
    </>
  );
};
